import { narrate, type Block, type NarrationSegment, type SpeechLabels, type SpeechReference } from '@w/content-blocks'
import { PAGE_TITLE_BLOCK_ID } from '@w/content-blocks-editor'

/** What narration reads of the workspace's pages. */
type NarrationPage = { id: string; parentId: string | null; title: string; language: string | null }

/** The page view as narration segments (`page-listening.spec.md` → Narration, Languages):
 * the title first, answers in the studied language, references read as the view labels them. */
export function narratePage({ pageId, title, blocks, pages, labels }: {
  pageId: string
  title: string
  blocks: Block[]
  /** The workspace's pages: language ancestors and reference titles. */
  pages: NarrationPage[]
  labels: SpeechLabels
}): NarrationSegment[] {
  return narrate([{ id: PAGE_TITLE_BLOCK_ID, type: 'pageTitle', title }, ...blocks], {
    labels,
    studied: studiedLanguage(pageId, pages),
    reference: block => spokenReference(pages, block),
  })
}

/** Its own language or its nearest language ancestor's; never a guess from spans. */
function studiedLanguage(pageId: string, pages: NarrationPage[]): string | undefined {
  for (let current = pages.find(candidate => candidate.id === pageId); current; current = pages.find(candidate => candidate.id === current?.parentId)) {
    if (current.language) return current.language
  }
  return undefined
}

function spokenReference(pages: NarrationPage[], block: SpeechReference): string {
  const title = (id: string) => pages.find(candidate => candidate.id === id)?.title.trim() ?? ''
  if (block.type === 'pages') return block.ids.map(title).filter(Boolean).join(', ')
  if (block.type !== 'mention') return title(block.pageId)
  if (block.subtype === 'date') return new Date(block.date).toLocaleDateString()
  return block.subtype === 'page' ? title(block.uid) : block.uid
}
